import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Calendar, Package, Award, Download, Search, X } from 'lucide-react';

const EtatClients = () => {
  const [clients, setClients] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    actifs: 0,
    nouveauxMois: 0,
    inactifs: 0,
    tauxRetention: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientHistory, setClientHistory] = useState([]);

  const loadData = async () => {
    try {
      // Charger tous les clients
      const clientsRes = await fetch('/api/clients');
      const clientsData = await clientsRes.json();
      const allClients = clientsData.success ? clientsData.data : [];

      // Charger toutes les réservations
      const reservationsRes = await fetch('/api/reservations');
      const reservationsData = await reservationsRes.json();
      const reservations = reservationsData.success ? reservationsData.data : [];

      // Calculer stats par client
      const clientsWithStats = allClients.map(client => {
        const clientResa = reservations.filter(r => r.code_client === client.code_client);
        const ca_total = clientResa.reduce((sum, r) => sum + parseFloat(r.sous_total || 0), 0);
        const nb_commandes = clientResa.length;
        
        // Dernière commande
        const derniereCommande = clientResa.length > 0 
          ? new Date(Math.max(...clientResa.map(r => new Date(r.created_at))))
          : null;

        // Client inactif si pas de commande depuis 3 mois
        const troismoisAgo = new Date();
        troismoisAgo.setMonth(troismoisAgo.getMonth() - 3);
        const estActif = derniereCommande && derniereCommande >= troismoisAgo;

        return {
          ...client,
          ca_total,
          nb_commandes,
          derniere_commande: derniereCommande,
          est_actif: estActif,
          panier_moyen: nb_commandes > 0 ? ca_total / nb_commandes : 0
        };
      });

      // Top 10 clients par CA
      const top10 = [...clientsWithStats]
        .sort((a, b) => b.ca_total - a.ca_total)
        .slice(0, 10);

      // Stats générales
      const unMoisAgo = new Date();
      unMoisAgo.setMonth(unMoisAgo.getMonth() - 1);
      const nouveauxMois = allClients.filter(c => new Date(c.created_at) >= unMoisAgo).length;
      const clientsActifs = clientsWithStats.filter(c => c.est_actif).length;
      const tauxRetention = allClients.length > 0 
        ? ((clientsActifs / allClients.length) * 100).toFixed(1) 
        : 0;

      setClients(clientsWithStats);
      setTopClients(top10);
      setStats({
        total: allClients.length,
        actifs: clientsActifs,
        nouveauxMois: nouveauxMois,
        inactifs: allClients.length - clientsActifs,
        tauxRetention: tauxRetention
      });

      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement données clients:', error);
      setLoading(false);
    }
  };

  const viewClientHistory = async (client) => {
    try {
      const response = await fetch(`/api/reservations/client/${client.code_client}`);
      const data = await response.json();
      setClientHistory(data.success ? data.data : []);
      setSelectedClient(client);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setClientHistory([]);
      setSelectedClient(client);
      setShowModal(true);
    }
  };

  const exportCSV = () => {
    const csvContent = clients.map(c => 
      `${c.nom_prenom},${c.email || 'N/A'},${c.telephone || 'N/A'},${c.nb_commandes},${c.ca_total.toFixed(2)},${c.panier_moyen.toFixed(2)}`
    ).join('\n');
    
    const blob = new Blob([`Nom,Email,Téléphone,Nb Commandes,CA Total,Panier Moyen\n${csvContent}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_stats_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clientsFiltres = clients.filter(c =>
    c.nom_prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    c.code_client.includes(searchTerm)
  );

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-500">Chargement des statistiques clients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-green-800">État des Clients</h2>
        <button
          onClick={exportCSV}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
        >
          <Download className="w-5 h-5" />
          Export CSV
        </button>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total clients</p>
              <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Clients actifs</p>
              <p className="text-2xl font-bold text-green-900">{stats.actifs}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-400" />
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Nouveaux (mois)</p>
              <p className="text-2xl font-bold text-purple-900">{stats.nouveauxMois}</p>
            </div>
            <Calendar className="h-8 w-8 text-purple-400" />
          </div>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Inactifs</p>
              <p className="text-2xl font-bold text-orange-900">{stats.inactifs}</p>
            </div>
            <Users className="h-8 w-8 text-orange-400" />
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Taux rétention</p>
              <p className="text-2xl font-bold text-yellow-900">{stats.tauxRetention}%</p>
            </div>
            <Award className="h-8 w-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Top 10 clients */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-yellow-500" />
          Top 10 clients par CA
        </h3>
        <div className="space-y-3">
          {topClients.map((client, index) => (
            <div 
              key={client.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
              onClick={() => viewClientHistory(client)}
            >
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  index === 0 ? 'bg-yellow-500 text-white' :
                  index === 1 ? 'bg-gray-400 text-white' :
                  index === 2 ? 'bg-orange-600 text-white' :
                  'bg-gray-200 text-gray-700'
                }`}>
                  {index + 1}
                </div>
                <div>
                  <div className="font-medium">{client.nom_prenom}</div>
                  <div className="text-sm text-gray-600">
                    {client.nb_commandes} commande(s) • Panier moyen: {client.panier_moyen.toFixed(2)}€
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-green-700">{client.ca_total.toFixed(2)}€</div>
                {client.derniere_commande && (
                  <div className="text-xs text-gray-500">
                    Dernière: {client.derniere_commande.toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recherche et liste complète */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4">Tous les clients</h3>
        
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commandes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CA Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Panier moyen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière cmd</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clientsFiltres.map(client => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="font-medium">{client.nom_prenom}</div>
                    <div className="text-sm text-gray-500">{client.email || 'Pas d\'email'}</div>
                  </td>
                  <td className="px-4 py-4 text-sm">{client.nb_commandes}</td>
                  <td className="px-4 py-4 text-sm font-semibold">{client.ca_total.toFixed(2)}€</td>
                  <td className="px-4 py-4 text-sm">{client.panier_moyen.toFixed(2)}€</td>
                  <td className="px-4 py-4 text-sm">
                    {client.derniere_commande 
                      ? client.derniere_commande.toLocaleDateString('fr-FR')
                      : 'Jamais'
                    }
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      client.est_actif 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {client.est_actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => viewClientHistory(client)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Voir historique
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Historique Client */}
      {showModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h3 className="text-lg font-semibold">{selectedClient.nom_prenom}</h3>
                <p className="text-sm text-gray-600">
                  {selectedClient.nb_commandes} commande(s) • CA total: {selectedClient.ca_total.toFixed(2)}€
                </p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <h4 className="font-semibold mb-4">Historique des commandes</h4>
              {clientHistory.length > 0 ? (
                <div className="space-y-3">
                  {clientHistory.map(resa => (
                    <div key={resa.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{resa.designation}</div>
                          <div className="text-sm text-gray-600">
                            {resa.quantite_reserve} {resa.unite}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(resa.created_at).toLocaleDateString('fr-FR')} - Période: {resa.periode_nom}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{parseFloat(resa.prix_ttc * resa.quantite_reserve).toFixed(2)}€</div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            resa.statut === 'livree' ? 'bg-green-100 text-green-800' :
                            resa.statut === 'preparee' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {resa.statut}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucune commande trouvée</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EtatClients;